import { Link, router, usePage } from '@inertiajs/react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import AppLayout from '@/layouts/app-layout'

interface RequestRow {
  uid: string
  page_path: string
  severity: string
  note: string
  requester_email: string | null
  status: string
  resolution_note: string | null
  created_at: string
  screenshot_url: string | null
  author: string | null
  round: number | null
}

interface Props {
  site: { uid: string; name: string }
  requests: RequestRow[]
  growbotik_built: boolean
}

export default function ChangeRequests({ site, requests, growbotik_built }: Props) {
  const { props } = usePage<{ flash?: { notice?: string; alert?: string } }>()
  const [notes, setNotes] = useState<Record<string, string>>({})

  function act(uid: string, event: 'start' | 'resolve' | 'decline') {
    router.patch(`/sites/${site.uid}/change_requests/${uid}`, {
      event,
      resolution_note: notes[uid] ?? '',
    })
  }

  function requestRebuild() {
    router.post(`/sites/${site.uid}/change_requests/request_rebuild`)
  }

  const open = requests.filter((r) => r.status === 'open' || r.status === 'in_progress')
  // Filed here (no author); Growbotik already has the ones it sent.
  const sendable = open.filter((r) => r.status === 'open' && !r.author)
  const closed = requests.filter((r) => r.status === 'resolved' || r.status === 'declined')

  return (
    <AppLayout
      title={`${site.name} — change requests`}
      actions={
        <Link className="text-primary text-[13px] font-medium hover:underline" href={`/sites/${site.uid}/content`}>
          Edit content
        </Link>
      }
    >
      {props.flash?.notice && (
        <p className="rounded-lg bg-primary/10 px-4 py-2 text-sm text-primary">{props.flash.notice}</p>
      )}
      {props.flash?.alert && (
        <p className="rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-800">{props.flash.alert}</p>
      )}

      {growbotik_built && (
        <div className="mb-6 flex max-w-3xl items-center justify-between gap-4 rounded-xl border p-4">
          <p className="text-muted-foreground text-sm">
            Growbotik built this site. Changes are made there: send the open requests and it rebuilds the draft.
          </p>
          <Button size="sm" disabled={sendable.length === 0} onClick={requestRebuild}>
            Ask Growbotik to rebuild ({sendable.length})
          </Button>
        </div>
      )}

      <div className="max-w-3xl">
        <h2 className="text-[17px] font-semibold" data-numeric>
          Open ({open.length})
        </h2>
        {open.length === 0 && <p className="text-muted-foreground mt-2 text-sm">Queue is clear.</p>}
        {open.map((r) => (
          <div key={r.uid} className="mt-4 rounded-xl border p-4">
            <div className="flex items-baseline justify-between gap-4">
              <strong className="text-sm font-semibold">
                {r.page_path} ·{' '}
                <span className={r.severity === 'major' ? 'text-amber-700' : 'text-muted-foreground'}>
                  {r.severity}
                </span>
              </strong>
              <span className="text-muted-foreground text-xs" data-numeric>
                {r.author ? `${r.author === 'hitm' ? 'Reviewer' : 'Client'}${r.round ? ` · round ${r.round}` : ''} · ` : ''}
                {r.status} · {r.created_at}
              </span>
            </div>
            <p className="mt-2 text-sm whitespace-pre-wrap">{r.note}</p>
            {r.screenshot_url && (
              <p className="mt-2 text-[13px]">
                <a className="text-primary font-medium hover:underline" href={r.screenshot_url} target="_blank" rel="noreferrer">
                  Screenshot
                </a>
              </p>
            )}
            {r.requester_email && (
              <p className="text-muted-foreground mt-2 text-[13px]">From: {r.requester_email}</p>
            )}
            <Input
              className="my-3"
              placeholder="Resolution note (sent to the requester)"
              value={notes[r.uid] ?? ''}
              onChange={(e) => setNotes((prev) => ({ ...prev, [r.uid]: e.target.value }))}
            />
            <div className="flex gap-2">
              {r.status === 'open' && (
                <Button variant="outline" size="sm" onClick={() => act(r.uid, 'start')}>
                  Start
                </Button>
              )}
              <Button size="sm" onClick={() => act(r.uid, 'resolve')}>
                Resolve
              </Button>
              <Button variant="outline" size="sm" onClick={() => act(r.uid, 'decline')}>
                Decline
              </Button>
            </div>
          </div>
        ))}

        <h2 className="mt-10 text-[17px] font-semibold" data-numeric>
          Closed ({closed.length})
        </h2>
        {closed.map((r) => (
          <div key={r.uid} className="mt-4 rounded-xl border p-4 opacity-70">
            <strong className="text-sm font-semibold">
              {r.page_path} · {r.status}
            </strong>
            <p className="mt-2 text-sm whitespace-pre-wrap">{r.note}</p>
            {r.resolution_note && (
              <p className="text-muted-foreground mt-2 text-[13px]">Resolution: {r.resolution_note}</p>
            )}
          </div>
        ))}
      </div>
    </AppLayout>
  )
}
